/**
 * Campaign Health — Superadmin view of all sites with campaigns enabled.
 * Shows bounce rates, auto-pause status, quota usage, domain verification.
 * Accessible only to Velaro admins (site 1032).
 *
 * Route: /admin/campaign-health
 */
import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface SiteHealth {
  siteId: number;
  autoPaused: boolean;
  autoPausedAt: string | null;
  autoPauseReason: string | null;
  bounceRatePct: number;
  recentSendCount: number;
  thisMonthSent: number;
  maxEmailsPerMonth: number;
  quotaPct: number;
  verifiedDomains: string[];
  hasVerifiedDomain: boolean;
  activeBroadcasts: number;
  bounceStatus: "healthy" | "warning" | "critical";
}

interface Summary {
  totalSites: number;
  pausedCount: number;
  noDomainCount: number;
  criticalCount: number;
}

export default function CampaignHealth() {
  const api = useApi();
  const [sites, setSites]     = useState<SiteHealth[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState<"all" | "paused" | "critical" | "nodomain">("all");

  const load = () => {
    setLoading(true);
    api.admin.get("DatabaseTool/CampaignHealth")
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setSummary(d.summary ?? null);
          setSites(Array.isArray(d.sites) ? d.sites : []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = sites.filter(s => {
    const matchSearch = !search || String(s.siteId).includes(search);
    if (!matchSearch) return false;
    if (filter === "paused")   return s.autoPaused;
    if (filter === "critical") return s.bounceStatus === "critical" || s.bounceStatus === "warning";
    if (filter === "nodomain") return !s.hasVerifiedDomain;
    return true;
  });

  const bounceColor = (status: string) =>
    status === "critical" ? "text-red-600 bg-red-50 border-red-200"
    : status === "warning" ? "text-amber-600 bg-amber-50 border-amber-200"
    : "text-green-600 bg-green-50 border-green-200";

  const quotaColor = (pct: number) =>
    pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-blue-500";

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Health</h1>
          <p className="text-sm text-gray-500 mt-1">All sites with outbound campaigns enabled · Velaro Superadmin only</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Sites with campaigns", value: summary.totalSites, color: "text-gray-900", bg: "bg-white" },
            { label: "Auto-paused", value: summary.pausedCount, color: summary.pausedCount > 0 ? "text-red-600" : "text-green-600", bg: summary.pausedCount > 0 ? "bg-red-50" : "bg-green-50" },
            { label: "No verified domain", value: summary.noDomainCount, color: summary.noDomainCount > 0 ? "text-amber-600" : "text-green-600", bg: summary.noDomainCount > 0 ? "bg-amber-50" : "bg-green-50" },
            { label: "Bounce rate issues", value: summary.criticalCount, color: summary.criticalCount > 0 ? "text-red-600" : "text-green-600", bg: summary.criticalCount > 0 ? "bg-red-50" : "bg-green-50" },
          ].map((c, i) => (
            <div key={i} className={`${c.bg} border border-gray-100 rounded-xl p-4 text-center`}>
              <p className={`text-3xl font-bold ${c.color}`}>{c.value}</p>
              <p className="text-xs text-gray-500 mt-1">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Campaign contact quota note */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <span className="text-blue-400 text-lg flex-shrink-0 mt-0.5">ℹ️</span>
        <div>
          <p className="text-sm font-semibold text-blue-800">Campaign contacts are tracked separately</p>
          <p className="text-xs text-blue-700 mt-0.5">
            Campaign contacts count against the <strong>campaign contact quota</strong> (maxContacts), not the chat/messaging contact quota.
            A customer can hit their campaign contact limit while still having available messaging contacts — and vice versa.
          </p>
          <a
            href="/admin/database-tool"
            className="text-xs text-blue-600 underline mt-1 inline-block hover:text-blue-800"
          >
            Manage campaign contacts in DB Tool →
          </a>
        </div>
      </div>

      {/* Alert bar — auto-paused sites need attention */}
      {(summary?.pausedCount ?? 0) > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-red-500 text-lg flex-shrink-0">🚨</span>
          <div>
            <p className="text-sm font-semibold text-red-800">{summary!.pausedCount} site{summary!.pausedCount > 1 ? "s" : ""} auto-paused due to high bounce rate</p>
            <p className="text-xs text-red-600 mt-0.5">
              Contact the customer, resolve the root cause (bad list, DNS misconfiguration), then clear AutoPaused in Campaign Settings for that site.
            </p>
          </div>
        </div>
      )}

      {/* Filter + search */}
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Search by Site ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-48"
        />
        <div className="flex gap-1.5">
          {([
            { id: "all",       label: "All Sites" },
            { id: "paused",    label: "🚨 Paused" },
            { id: "critical",  label: "⚠️ Bounce Issues" },
            { id: "nodomain",  label: "🔒 No Domain" },
          ] as const).map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium ${filter === f.id ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{filtered.length} site{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading campaign health data…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No sites match this filter.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(site => (
            <div
              key={site.siteId}
              className={`bg-white border rounded-xl p-4 ${site.autoPaused ? "border-red-200 ring-1 ring-red-100" : site.bounceStatus !== "healthy" ? "border-amber-200" : "border-gray-100"}`}
            >
              <div className="flex flex-wrap items-start gap-4">
                {/* Site ID + status */}
                <div className="flex-shrink-0 min-w-[120px]">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900">Site {site.siteId}</span>
                    {site.autoPaused && (
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">PAUSED</span>
                    )}
                  </div>
                  {site.activeBroadcasts > 0 && (
                    <span className="text-xs text-blue-600">{site.activeBroadcasts} active broadcast{site.activeBroadcasts > 1 ? "s" : ""}</span>
                  )}
                </div>

                {/* Bounce rate */}
                <div className={`flex-shrink-0 border rounded-lg px-3 py-1.5 text-center min-w-[90px] ${bounceColor(site.bounceStatus)}`}>
                  <p className="text-xl font-bold">{site.bounceRatePct}%</p>
                  <p className="text-xs">Bounce rate</p>
                  <p className="text-[10px] opacity-70">{site.recentSendCount} sends</p>
                </div>

                {/* Monthly quota */}
                <div className="flex-1 min-w-[160px]">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Monthly quota</span>
                    <span className="font-medium text-gray-700">
                      {site.thisMonthSent.toLocaleString()} / {site.maxEmailsPerMonth > 0 ? site.maxEmailsPerMonth.toLocaleString() : "∞"}
                    </span>
                  </div>
                  {site.maxEmailsPerMonth > 0 && (
                    <div className="h-2 bg-gray-100 rounded-full">
                      <div
                        className={`h-2 rounded-full transition-all ${quotaColor(site.quotaPct)}`}
                        style={{ width: `${Math.min(100, site.quotaPct)}%` }}
                      />
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {site.maxEmailsPerMonth > 0 ? `${site.quotaPct}% used` : "Unlimited"}
                  </p>
                </div>

                {/* Domain status */}
                <div className="flex-shrink-0 min-w-[140px]">
                  {site.hasVerifiedDomain ? (
                    <div>
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                        Verified domain
                      </span>
                      <div className="mt-1 space-y-0.5">
                        {site.verifiedDomains.map(d => (
                          <span key={d} className="block text-[10px] text-gray-500 font-mono">{d}</span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                      No verified domain
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex-shrink-0 flex flex-col gap-1.5">
                  <a
                    href={`/admin/database-tool?site=${site.siteId}`}
                    className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 text-center"
                  >
                    Open in DB Tool
                  </a>
                </div>
              </div>

              {/* Auto-pause details */}
              {site.autoPaused && site.autoPauseReason && (
                <div className="mt-3 pt-3 border-t border-red-100">
                  <p className="text-xs text-red-700">
                    <strong>Paused {site.autoPausedAt ? new Date(site.autoPausedAt).toLocaleString() : ""}:</strong> {site.autoPauseReason}
                  </p>
                  <p className="text-xs text-red-500 mt-1">
                    To resume: resolve root cause → Database Tool → Campaign Settings → Site {site.siteId} → clear AutoPaused
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
